/**
 * Utility to detect and work with workspace subdomains and custom domains.
 * Root domain is configured via VITE_ROOT_DOMAIN env var.
 */

const ROOT_DOMAIN = import.meta.env.VITE_ROOT_DOMAIN || "";

/**
 * Extract subdomain from current hostname.
 * Returns null if on root domain or localhost.
 */
export function getSubdomain(): string | null {
  if (!ROOT_DOMAIN) return null;
  const hostname = window.location.hostname;
  if (hostname === ROOT_DOMAIN || hostname === `www.${ROOT_DOMAIN}`) return null;
  if (hostname.endsWith(`.${ROOT_DOMAIN}`)) {
    return hostname.replace(`.${ROOT_DOMAIN}`, "");
  }
  return null;
}

/**
 * Detect if current hostname is a custom domain (not ROOT_DOMAIN, not a subdomain of it, not localhost).
 * Returns the hostname if it's a custom domain, null otherwise.
 */
export function getCustomDomain(): string | null {
  const hostname = window.location.hostname;
  // Localhost / dev environments
  if (hostname === "localhost" || hostname === "127.0.0.1" || hostname.endsWith(".lovable.app") || hostname.endsWith(".lovableproject.com")) return null;
  if (!ROOT_DOMAIN) return null;
  // Root domain or subdomain of root domain
  if (hostname === ROOT_DOMAIN || hostname === `www.${ROOT_DOMAIN}` || hostname.endsWith(`.${ROOT_DOMAIN}`)) return null;
  // Everything else is a custom domain
  return hostname;
}

/**
 * Returns true if the current hostname is a workspace context (subdomain or custom domain).
 */
export function isWorkspaceHost(): boolean {
  return getSubdomain() !== null || getCustomDomain() !== null;
}

/**
 * Build a public landing URL, using subdomain if available.
 */
export function buildPublicUrl(slug: string, subdomain?: string | null, customDomain?: string | null): string {
  const protocol = window.location.protocol;
  // Prefer custom domain if available
  if (customDomain) {
    return `${protocol}//${customDomain}/l/${slug}`;
  }
  if (subdomain && ROOT_DOMAIN) {
    return `${protocol}//${subdomain}.${ROOT_DOMAIN}/l/${slug}`;
  }
  const base = import.meta.env.VITE_PUBLIC_DOMAIN || window.location.origin;
  return `${base}/l/${slug}`;
}
